// Auto-diagnostic LLM — URL du service conversationnel qui remplace l'embed
// Tally (décisions D-b/D-c). Même logique de résolution par environnement que
// src/utils/apiBaseUrl.js : détection sur window.location.hostname.
//
// Contrat de redirection :
//   GET {base}/start?booking_uuid=<uuid v4>&return_url=<url encodée>&origin=front&position=<adresse>
// Au terme du diagnostic, le service redirige vers return_url en ajoutant
// booking_uuid=<uuid>&autodiag_status=completed&capabilities[0][id]=N&… —
// format déjà consommé par parseCapabilitiesFromURL (src/utils/utils.jsx).

const LOCAL = 'http://localhost:3030';
const STAGING = 'https://staging-autodiag.roulezjeunesse.com';
const PROD = 'https://autodiag.roulezjeunesse.com';

// ROLLBACK : poser AUTODIAG_LLM_ENABLED=0 dans Vercel (scope Production) puis
// redéployer → Tally direct, sans écran de choix et sans AUCUN événement, sans
// commit ni PR. Absente ou toute autre valeur → parcours LLM (les previews et
// staging n'ont rien à configurer). Inlinée AU BUILD côté client (DefinePlugin,
// webpack.client.js) : un changement de variable exige un redeploy pour prendre
// effet — c'est le même modèle que API_URL. Override ponctuel par query param
// sur /reparation/autodiag : `?autodiag_llm=0` force l'ancien Tally,
// `?autodiag_llm=1` force le nouveau parcours (utile pour tester chaque chemin
// quel que soit le flag).
export const AUTODIAG_LLM_ENABLED = process.env.AUTODIAG_LLM_ENABLED !== '0';

export function isAutodiagLlmEnabled(search) {
  const override = new URLSearchParams(search || '').get('autodiag_llm');
  if (override === '0') return false;
  if (override === '1') return true;
  return AUTODIAG_LLM_ENABLED;
}

// ROLLBACK embed : AUTODIAG_EMBED_ENABLED=0 dans Vercel + redeploy → le choix
// « assistant » revient à la redirection pleine page vers le service
// (comportement FIX-429) au lieu de l'iframe AutodiagFrame (contrat AD-45).
// Levier intermédiaire : si SEUL l'embed pose problème en prod (cookie,
// postMessage…), on garde l'assistant sans revenir à Tally. Même mécanique
// build-time que AUTODIAG_LLM_ENABLED ci-dessus. Override ponctuel :
// `?autodiag_embed=0` force la redirection, `?autodiag_embed=1` force l'iframe.
export const AUTODIAG_EMBED_ENABLED =
  process.env.AUTODIAG_EMBED_ENABLED !== '0';

export function isAutodiagEmbedEnabled(search) {
  const override = new URLSearchParams(search || '').get('autodiag_embed');
  if (override === '0') return false;
  if (override === '1') return true;
  return AUTODIAG_EMBED_ENABLED;
}

export function getAutodiagBase() {
  if (typeof window !== 'undefined' && window.location) {
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname === '0.0.0.0') return LOCAL;
    if (hostname === 'staging.roulezjeunesse.com') return STAGING;
    // Previews Vercel : router vers le service de staging pour ne jamais
    // écrire d'événements d'exposition dans l'event log de production
    // (cohérent avec API_URL scope Preview → API staging).
    if (hostname.endsWith('.vercel.app')) return STAGING;
    return PROD;
  }
  // SSR : jamais utilisé pour rediriger (la redirection est client-side),
  // on renvoie la prod par défaut.
  return PROD;
}

export function buildAutodiagStartUrl({ bookingUuid, returnUrl, position }) {
  const params = new URLSearchParams({
    booking_uuid: bookingUuid,
    return_url: returnUrl,
    origin: 'front',
    position: position || '',
  });
  return `${getAutodiagBase()}/start?${params.toString()}`;
}

// UUID v4 : crypto.randomUUID quand dispo (contextes sécurisés, navigateurs
// récents), sinon fallback Math.random (même implémentation que l'ancien
// TallyForm).
export function generateBookingUuid() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Slot sessionStorage qui stabilise le booking_uuid pour la durée d'un passage
// de tunnel dans un même onglet. Invariant : un humain = un uuid par passage ;
// un remontage in-funnel (CSSTransition unmountOnExit de Repair.jsx, retour
// depuis /reparation/date) ne régénère JAMAIS — sinon la dédup analytics
// (event, uuid) devient inopérante et choice_viewed/flow_chosen sont ré-émis
// pour le même humain (FIX-429, review post-PR).
// Limite assumée : deux réservations successives dans le même onglet partagent
// le slot (les events de la seconde sont dédupliqués) — cas rare, documenté.
export const BOOKING_UUID_SESSION_KEY = 'rj_autodiag_booking_uuid';

const UUID_V4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// Résout l'UUID du passage de tunnel courant. Priorité :
//   1. candidateUuid (booking_uuid déjà dans le store redux) s'il est valide
//      ET issu de la même session d'onglet (== slot sessionStorage) ;
//   2. le slot sessionStorage s'il est valide ;
//   3. génération + enregistrement dans le slot.
// Tout accès sessionStorage sous try/catch (SSR, Safari privé) — en dernier
// recours on retombe sur un uuid frais non persisté (comportement pré-fix).
export function resolveTunnelBookingUuid(candidateUuid) {
  let slot = null;
  try {
    slot = sessionStorage.getItem(BOOKING_UUID_SESSION_KEY);
  } catch (e) {
    slot = null;
  }
  if (
    candidateUuid &&
    UUID_V4_RE.test(candidateUuid) &&
    candidateUuid === slot
  ) {
    return candidateUuid;
  }
  if (slot && UUID_V4_RE.test(slot)) {
    return slot;
  }
  const fresh = generateBookingUuid();
  try {
    sessionStorage.setItem(BOOKING_UUID_SESSION_KEY, fresh);
  } catch (e) {
    // sessionStorage indisponible : uuid frais par montage (pré-fix).
  }
  return fresh;
}
